import { createElement } from "react";
import { ClockProvider, type ClockSnapshot } from "@sx/ui/clock";
import type { QueryClient } from "@tanstack/react-query";
import type { RouterHistory } from "@tanstack/react-router";
import { createRouter } from "@tanstack/react-router";
import { makeQueryClient } from "@sx/api-client";

import { reportFailure, reportRenderIncident } from "./lib/incident";
import { routeTree } from "./routeTree.gen";
import { NotFound } from "./shell/NotFound";
import { RouteError } from "./shell/RouteError";
import { NAVIGATION_WAIT } from "./shell/RouteFallback";

/** Build the console's router.
 *
 * A factory, not a module singleton, because the smoke suite builds one per case with a
 * memory history and its own query cache — a shared router would leak the previous test's
 * location and data into the next one.
 *
 * The shared client repairs a transient read twice before anyone is told, then keeps
 * repairing it in the background for as long as it stays mounted; reads are fresh for
 * thirty seconds, so a navigation back into a workspace paints from memory. Mutations are
 * single-attempt unless their call site carries a stable idempotency key and explicitly
 * opts into retry.
 *
 * Every failed read or write is recorded at the cache, not at the screen that shows it: a
 * page cannot forget to report what it never had to report.
 *
 * Persistence is injected rather than assumed: `main.tsx` passes
 * `persistConsoleQueryCache`, and a suite that builds a router per case passes nothing
 * and cannot inherit the previous case's snapshot from disk. It happens *here* rather
 * than after `createRouter` because hydration is synchronous and must land before the
 * first render; a snapshot restored after `render` would be a cold start followed by a
 * flicker. */
export function createConsoleRouter(
  history?: RouterHistory,
  persist: (queryClient: QueryClient) => unknown = () => undefined,
  pathname?: string,
  rendered?: Promise<void>,
) {
  const queryClient = makeQueryClient({}, (failure) => reportFailure(failure, pathname));
  persist(queryClient);
  let renderClock: ClockSnapshot = { clock: "utc", timeZone: "UTC", now: Date.now() };
  return createRouter({
    routeTree,
    history,
    context: { queryClient },
    dehydrate: async () => {
      const { serializeQueryCache } = await import("@sx/api-client/request-cache");
      // TanStack calls dehydrate before rendering components. Keep the nested
      // promise pending until their query reads settle; Seroval transfers it without
      // blocking the request handler from entering the render callback.
      const queries = (async () => {
        await rendered;
        return serializeQueryCache(queryClient);
      })();
      return { queries, clock: renderClock };
    },
    hydrate: async (state) => {
      const { hydrateQueryCache } = await import("@sx/api-client/request-cache");
      hydrateQueryCache(queryClient, await state.queries);
      renderClock = state.clock;
    },
    Wrap: ({ children }) => createElement(ClockProvider, { snapshot: renderClock, children }),
    // Prefetch on hover/focus: a workspace's chunk starts downloading while the pointer is
    // still travelling to it, which is what makes code splitting invisible.
    defaultPreload: "intent",
    // A navigation never draws a fallback: the page being left stays on screen until the
    // next one can draw its own frame. Browser loaders start reads without waiting;
    // server loaders finish essential reads before emitting the private document.
    // A frame alone does not satisfy the page's essential-data readiness budget.
    // The one browser wait that is drawn
    // is the first: `_console` names its own pending frame for the cold load, when there
    // is no old page to keep.
    ...NAVIGATION_WAIT,
    defaultNotFoundComponent: NotFound,
    // A render that throws is caught here or it is caught by the router's own bare
    // fallback — there is no third option, and the bare one reported nothing. `onCatch` is
    // the only hook that sees the failure at all: an error boundary swallows the throw
    // before `window.onerror`, so PostHog's unhandled-error capture never sees it.
    defaultErrorComponent: RouteError,
    defaultOnCatch: (error, errorInfo) => {
      reportRenderIncident(error, {
        surface: "route",
        pathname,
        componentStack: errorInfo.componentStack ?? undefined,
      });
    },
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createConsoleRouter>;
  }
}
